import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ACTIONS, allowedActions, api, ApiError, fmtTime, type HistoryEntry, type Issue, type Occurrence } from "../api";
import { useSession } from "../session";
import { StatusBadge } from "./Issues";

function Stack({ frames }: { frames: Occurrence["stacktrace"] }) {
  if (!frames?.length) return null;
  return (
    <ol className="stack">
      {[...frames].reverse().map((f, i) => (
        <li key={i} className={f.in_app ? "in-app" : "lib"}>
          <code>{f.module ?? f.filename}:{f.lineno}</code> in <code>{f.function}</code>
          {f.context_line && <pre>{f.context_line}</pre>}
        </li>
      ))}
    </ol>
  );
}

function Payload({ issue }: { issue: Issue }) {
  const [show, setShow] = useState(false);
  const q = useQuery({
    queryKey: ["payload", issue.id, issue.last_payload_id],
    queryFn: () => api<{ data: unknown; encoding: string; size: number; content_type: string }>(`/v1/issues/${issue.id}/payload`),
    enabled: show,
  });
  if (!issue.last_payload_id) return null;
  return (
    <section aria-labelledby="payload-title">
      <h2 id="payload-title">Stored payload</h2>
      {!show ? (
        <button type="button" onClick={() => setShow(true)}>Show payload</button>
      ) : q.data ? (
        <>
          <p className="muted small">{q.data.content_type} · {q.data.encoding} · {q.data.size} bytes</p>
          <pre className="payload">{typeof q.data.data === "string" ? q.data.data : JSON.stringify(q.data.data, null, 2)}</pre>
        </>
      ) : (
        <p role="status">Loading…</p>
      )}
    </section>
  );
}

export function IssueDetailPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const { can } = useSession();
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const issue = useQuery({ queryKey: ["issue", id], queryFn: () => api<Issue>(`/v1/issues/${id}`) });
  const occ = useQuery({
    queryKey: ["occurrences", id],
    queryFn: () => api<{ occurrences: Occurrence[] }>(`/v1/issues/${id}/occurrences`, { query: { limit: 20 } }),
  });
  const history = useQuery({
    queryKey: ["history", id],
    queryFn: () => api<{ history: HistoryEntry[] }>(`/v1/issues/${id}/history`),
  });
  const act = useMutation({
    mutationFn: (action: string) => api(`/v1/issues/${id}/actions`, { method: "POST", body: { action, note: note || undefined } }),
    onSuccess: () => {
      setNote("");
      setError(null);
      for (const k of ["issue", "history", "issues"]) qc.invalidateQueries({ queryKey: [k] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  if (issue.isLoading) return <p role="status">Loading…</p>;
  if (issue.isError || !issue.data) return <p role="alert" className="error">Issue not found.</p>;
  const i = issue.data;
  const canManage = can("errors:manage");

  return (
    <article aria-labelledby="issue-title">
      <p><Link to="/issues">← Issues</Link></p>
      <h1 id="issue-title">{i.title}</h1>
      <p className="row">
        <StatusBadge status={i.status} regressed={i.regressed} />
        <span>{i.kind}</span>
        <span>{i.group} / {i.log_group}</span>
        <span>seen {i.count}×</span>
        {i.kind === "processing" && <span>attempts {i.attempts}{i.max_attempts ? ` / ${i.max_attempts}` : ""}</span>}
      </p>
      <dl className="facts">
        <dt>First seen</dt><dd>{fmtTime(i.first_seen)}</dd>
        <dt>Last seen</dt><dd>{fmtTime(i.last_seen)}</dd>
        <dt>Fingerprint</dt><dd><code>{i.fingerprint}</code></dd>
        {i.component && (<><dt>Component</dt><dd>{i.component}{i.operation ? `.${i.operation}` : ""}</dd></>)}
        {i.item_key && (<><dt>Item</dt><dd><code>{i.item_key}</code></dd></>)}
        <dt>Status changed</dt><dd>{fmtTime(i.status_changed_at)} by {i.status_changed_by}</dd>
      </dl>

      {canManage && (
        <section aria-labelledby="actions-title" className="card">
          <h2 id="actions-title">Actions</h2>
          <label htmlFor="note">Note (optional)</label>
          <textarea id="note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
          <div className="row">
            {allowedActions(i).map((a) => (
              <button key={a} type="button" disabled={act.isPending} onClick={() => act.mutate(a)}>
                {ACTIONS[a].label}
              </button>
            ))}
            <button type="button" disabled={!note || act.isPending} onClick={() => act.mutate("comment")}>Add note</button>
          </div>
          {error && <p role="alert" className="error">{error}</p>}
        </section>
      )}

      <Payload issue={i} />

      <section aria-labelledby="occ-title">
        <h2 id="occ-title">Recent occurrences</h2>
        {occ.data?.occurrences.map((o) => (
          <details key={o.event_id} className="occurrence">
            <summary>
              {fmtTime(o.timestamp)} · {o.error_type}: {o.message}
              {o.attempt ? ` · attempt ${o.attempt}` : ""}{o.release ? ` · ${o.release}` : ""}
            </summary>
            <Stack frames={o.stacktrace} />
            {o.context && <pre className="fields">{JSON.stringify(o.context, null, 2)}</pre>}
            {o.tags && <p className="small">tags: {Object.entries(o.tags).map(([k, v]) => `${k}=${v}`).join(", ")}</p>}
          </details>
        ))}
      </section>

      <section aria-labelledby="hist-title">
        <h2 id="hist-title">History</h2>
        <ol className="history">
          {history.data?.history.map((h) => (
            <li key={h.id}>
              <time>{fmtTime(h.at)}</time> <strong>{h.action}</strong>
              {h.from !== h.to && ` ${h.from ?? "∅"} → ${h.to}`} <span className="muted">by {h.actor}</span>
              {h.note && <blockquote>{h.note}</blockquote>}
            </li>
          ))}
        </ol>
      </section>
    </article>
  );
}
